import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { get, post } from '../utils/apiCalls';
import type { LoginRequest, LoginResponse, ProfileResponse } from '../../types/models';
import type { Whitelabel } from '../../types/models';

export interface UserInfo {
  userId: string;
  username: string;
  role: 'ADMIN' | 'USER';
  profileImage?: string;
  whitelabels?: Whitelabel[];
}

interface AuthContextValue {
  isAuthenticated: boolean;
  user: UserInfo | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function decodeJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split('.');
  if (parts.length !== 3) {
    return {};
  }
  const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(atob(payload));
}

function extractUserFromToken(): UserInfo | null {
  const token = localStorage.getItem('token');
  const expiry = localStorage.getItem('tokenExpirationDate');
  if (!token || !expiry || new Date(expiry).getTime() <= Date.now()) {
    return null;
  }
  const claims = decodeJwtPayload(token);
  return {
    userId: (claims.USER_ID as string) || '',
    username: (claims.sub as string) || '',
    role: (claims.ROLE as 'ADMIN' | 'USER') || 'USER',
  };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserInfo | null>(extractUserFromToken);

  const fetchProfile = useCallback(async () => {
    try {
      const profile = await get<ProfileResponse>('/users/me');
      setUser((prev) => {
        if (!prev) return prev;
        return { ...prev, profileImage: profile.profileImage, whitelabels: profile.whitelabels };
      });
    } catch {
      // profile fetch is best-effort
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchProfile();
    }
  }, [user?.userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (username: string, password: string) => {
    const body: LoginRequest = { username, password };
    const data = await post<LoginResponse>('/auth/login', body);
    localStorage.setItem('token', data.token!);
    localStorage.setItem('tokenExpirationDate', data.tokenExpirationDate!);
    setUser(extractUserFromToken());
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('tokenExpirationDate');
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      isAuthenticated: user !== null,
      user,
      login,
      logout,
      refreshProfile: fetchProfile,
    }),
    [user, login, logout, fetchProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
